import React from 'react';
import { Chip } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { styled } from '@mui/material/styles';

/**
 * PillBadge Size
 */
export type PillBadgeSize = 'small' | 'medium';

/**
 * Icon Position
 */
export type IconPosition = 'left' | 'right';

/**
 * PillBadge Props
 * 
 * Atomic component - accepts explicit style props, no domain knowledge.
 * Built on MUI Chip: https://mui.com/material-ui/react-chip/
 */
export interface PillBadgeProps {
  /** Badge content (text or elements) */
  children: React.ReactNode;
  
  /** Badge size - maps to MUI Chip size */
  size?: PillBadgeSize;
  
  /** Background color */
  backgroundColor?: string;
  
  /** Text color */
  textColor?: string;
  
  /** Border color (optional - no border if not provided) */
  borderColor?: string;
  
  /** Optional icon element */
  icon?: React.ReactNode;
  
  /** Icon position relative to text */
  iconPosition?: IconPosition;
  
  /** Additional CSS class */
  className?: string;
  
  /** ARIA label for accessibility */
  ariaLabel?: string;

  /** Style overrides (merged last, so they win over the defaults above). */
  sx?: SxProps<Theme>;
}

interface StyledChipProps {
  bgColor: string;
  txtColor: string;
  brdColor?: string;
  isIconRight: boolean;
}

const StyledChip = styled(Chip, {
  shouldForwardProp: prop => 
    !['bgColor', 'txtColor', 'brdColor', 'isIconRight'].includes(prop as string),
})<StyledChipProps>(({ bgColor, txtColor, brdColor, isIconRight }) => ({
  // Layout
  display: 'inline-flex',
  alignItems: 'center',
  flexDirection: isIconRight ? 'row-reverse' : 'row',
  gap: 4,
  padding: '6px 10px',
  height: 'auto',
  maxWidth: 'none',
  
  // Colors
  backgroundColor: bgColor,
  color: txtColor,
  border: brdColor ? `1px solid ${brdColor}` : 'none',
  
  // Typography
  fontSize: 12,
  fontWeight: 500,
  borderRadius: 12,
  
  // Label
  '& .MuiChip-label': {
    padding: 0,
    overflow: 'visible',
  },
  
  // Icon
  '& .MuiChip-icon': {
    margin: 0,
    color: 'inherit',
  },
}));

/**
 * PillBadge Component
 * 
 * A reusable pill-shaped badge component built on MUI Chip.
 * 
 * Layer 1: Atomic Component
 * - Pure presentation, no domain knowledge
 * - Accepts explicit style props
 * 
 * @see https://mui.com/material-ui/react-chip/
 * 
 * @example
 * ```tsx
 * // Simple badge
 * <PillBadge backgroundColor="#F3F6FA" textColor="#0C0F24">
 *   Active
 * </PillBadge>
 * 
 * // Badge with icon
 * <PillBadge 
 *   backgroundColor="#32984E" 
 *   textColor="#FFFFFF"
 *   icon={<OpenHouseIcon />}
 * >
 *   Open House
 * </PillBadge>
 * ```
 */
export default function PillBadge({
  children,
  size = 'small',
  backgroundColor = '#F3F6FA',
  textColor = '#0C0F24',
  borderColor,
  icon,
  iconPosition = 'left',
  className,
  ariaLabel,
  sx,
}: PillBadgeProps) {
  const isIconRight = iconPosition === 'right' && !!icon;

  return (
    <StyledChip
      label={children}
      size={size}
      icon={icon ? (icon as React.ReactElement) : undefined}
      className={className}
      aria-label={ariaLabel}
      bgColor={backgroundColor}
      txtColor={textColor}
      brdColor={borderColor}
      isIconRight={isIconRight}
      sx={sx}
    />
  );
}
